import { React, useContext, useState, useEffect, useId } from "react";
import { AppContext } from "../App";
import { FiArrowRight, FiInbox, FiBriefcase, FiMapPin, FiClock, FiFileText, FiChevronRight, FiGrid, FiList } from "react-icons/fi";
import bannerImg from "../assets/banner1.jpg"; 
import { callApi } from "../Helpers";

export default function CareerContent() {
  const appContext = useContext(AppContext);
  const scopeId = useId().replace(/:/g, "");
  
  const [allJobs, setAllJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    getAllJobs();
    if (appContext?.setReady) appContext.setReady(true);
    if (appContext?.setLoading) appContext.setLoading(false);
  }, [appContext]);

  function view(uuid) {
    appContext?.navTo?.({
      item: 'side-view',
      subItem: 'job-options', 
      extraItem: uuid,
    });
  }

  function openMyApplications() {
    appContext?.navTo?.({
      item: 'side-view',
      subItem: 'my-applications'
    });
  }

  async function getAllJobs() {
    setIsLoading(true);
    try {
      const response = await callApi('jobapplicant/get-open-jobs', {});
      if (response && response.status === 1) {
        setAllJobs(response.data);
      } else {
        setAllJobs([]);
      }
    } catch (err) {
      setAllJobs([]);
    } finally {
      setIsLoading(false);
    }
  }

  const pillars = [
    { tag: "// 01", title: "Stack Rigor", desc: "Building architectures using foundational computer science paradigms and reactive tech stacks." },
    { tag: "// 02", title: "Product Agency", desc: "Engineers at Werenium don't just clear tickets; they scope systems and own functional vertical roadmaps." },
    { tag: "// 03", title: "Global Systems", desc: "Deploy code that handles asynchronous high-load processing operations across cloud infrastructure clusters." },
  ];

  return (
    <>
      <style>{`
        .${scopeId}-root {
          background-color: #050b11;
          color: #f3f4f6;
          min-height: 100vh;
          font-family: system-ui, -apple-system, sans-serif;
        }

        /* --- Split Grid Section --- */
        .${scopeId}-split-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          min-height: 80vh;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }

        .${scopeId}-left-pane {
          padding: 80px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          border-right: 1px solid rgba(255, 255, 255, 0.08);
          position: relative;
        }

        .${scopeId}-right-pane {
          position: relative;
          background: url(${bannerImg}) center/cover no-repeat;
        }

        .${scopeId}-pane-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(5, 11, 17, 0.95) 20%, rgba(5, 11, 17, 0.4) 100%);
          mix-blend-mode: multiply;
        }

        .${scopeId}-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 6px 16px;
          border-radius: 100px;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 2px;
          color: var(--secondaryColor, #fff);
          width: fit-content;
          margin-bottom: 30px;
        }

        .${scopeId}-headline {
          font-size: clamp(2.5rem, 4vw, 4rem);
          font-weight: 800;
          line-height: 1.1;
          letter-spacing: -0.04em;
          margin-bottom: 30px;
          color: #ffffff;
        }

        /* --- Culture Pillars List --- */
        .${scopeId}-pillar-list {
          margin-top: 40px;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .${scopeId}-pillar-row {
          display: flex;
          gap: 25px;
          padding: 20px;
          border-radius: 6px;
          cursor: pointer;
          background: rgba(255,255,255, 0);
          transition: all 0.3s ease;
        }

        .${scopeId}-pillar-row.${scopeId}-active,
        .${scopeId}-pillar-row:hover {
          background: rgba(255, 255, 255, 0.03);
        }

        .${scopeId}-pillar-num {
          font-family: monospace;
          color: var(--secondaryColor, rgba(255,255,255,0.4));
          font-size: 0.85rem;
          margin-top: 3px;
        }

        .${scopeId}-pillar-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 6px;
        }

        .${scopeId}-pillar-desc {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.6);
          line-height: 1.5;
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease, opacity 0.3s ease;
          opacity: 0;
        }

        .${scopeId}-pillar-row.${scopeId}-active .${scopeId}-pillar-desc {
          max-height: 100px;
          opacity: 1;
          margin-top: 10px;
        }

        /* --- Main Operational Section --- */
        .${scopeId}-main-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 100px 40px;
        }

        .${scopeId}-board-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 50px;
          gap: 20px;
        }

        /* --- Linear Row-Styled Openings --- */
        .${scopeId}-matrix-list {
          display: flex;
          flex-direction: column;
          gap: 12px; /* Adds explicit padding breaks between mobile cards before condensing */
          background: transparent;
        }

        .${scopeId}-matrix-item {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 16px;
          background: #091118;
          padding: 20px;
          border-radius: 6px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease;
        }

        .${scopeId}-matrix-item:hover {
          background: #0d1924;
          border-color: rgba(255, 255, 255, 0.2);
        }

        .${scopeId}-job-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #ffffff;
          margin: 0;
          line-height: 1.3;
        }

        .${scopeId}-item-meta-group {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          gap: 16px;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
          padding-top: 14px;
        }

        .${scopeId}-cell-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.5);
          font-weight: 500;
        }

        .${scopeId}-action-trigger {
          color: var(--secondaryColor, #fff);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .${scopeId}-matrix-item:hover .${scopeId}-action-trigger {
          background: #ffffff;
          color: #050b11;
          transform: translateX(2px);
        }

        /* --- Modern Minimalist Empty State --- */
        .${scopeId}-clean-empty {
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 6px;
          background: #091118;
          padding: 60px;
          display: grid;
          grid-template-columns: 1.5fr 2fr;
          gap: 60px;
          align-items: center;
        }

        .${scopeId}-empty-left {
          border-right: 1px solid rgba(255, 255, 255, 0.08);
          padding-right: 40px;
        }

        .${scopeId}-empty-right {
          padding-left: 20px;
        }

        .${scopeId}-action-link {
          background: none;
          border: none;
          color: var(--secondaryColor, #fff);
          font-size: 0.9rem;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 0;
          flex-shrink: 0;
        }

        .${scopeId}-action-link:hover {
          text-decoration: underline;
        }

        .${scopeId}-email-block {
          display: inline-flex;
          align-items: center;
          gap: 15px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          padding: 12px 24px;
          border-radius: 4px;
          color: #ffffff;
          text-decoration: none;
          font-family: monospace;
          font-size: 0.9rem;
          transition: border-color 0.2s ease;
        }

        .${scopeId}-email-block:hover {
          border-color: rgba(255,255,255,0.3);
        }

        /* --- Responsive Viewports Matrix --- */
        @media (min-width: 768px) {
          .${scopeId}-matrix-list {
            gap: 1px;
            background: rgba(255, 255, 255, 0.08);
            border-radius: 6px;
            overflow: hidden;
            border: 1px solid rgba(255, 255, 255, 0.08);
          }

          .${scopeId}-matrix-item {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            background: #091118;
            padding: 24px 30px;
            border-radius: 0;
            border: none;
            gap: 30px;
          }

          .${scopeId}-item-meta-group {
            width: auto;
            justify-content: flex-end;
            gap: 24px;
            border-top: none;
            padding-top: 0;
          }
          
          .${scopeId}-action-trigger {
            width: 36px;
            height: 36px;
          }
        }

        @media (max-width: 992px) {
          .${scopeId}-split-grid { grid-template-columns: 1fr; min-height: auto; }
          .${scopeId}-right-pane { display: none; }
          .${scopeId}-left-pane { padding: 40px 20px; }
          .${scopeId}-clean-empty { grid-template-columns: 1fr; gap: 30px; padding: 30px 20px; }
          .${scopeId}-empty-left { border-right: none; padding-right: 0; border-bottom: 1px solid rgba(255,255,255,0.08); padding-bottom: 20px; }
        }

        @media (max-width: 768px) {
          .${scopeId}-main-content { padding: 60px 20px; }
          .${scopeId}-board-header { flex-direction: column; align-items: flex-start; gap: 20px; }
        }
      `}</style>

      <div className={`${scopeId}-root`}>
        
        {/* Live Positions Terminal Block */}
        <main className={`${scopeId}-main-content`}>
          <div className={`${scopeId}-board-header pt-3`}>
            <div>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 800, margin: 0 }}>Available Roles</h2>
              <p style={{ color: 'rgba(255,255,255,0.4)', margin: '5px 0 0 0', fontSize: '0.9rem' }}>Select an open track to evaluate structural prerequisites.</p>
            </div>
            
            <button className={`${scopeId}-action-link`} onClick={openMyApplications}>
              <FiFileText size={16} /> Track Submissions
            </button>
          </div>

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '100px 0', color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>
              Loading...
            </div>
          ) : allJobs && allJobs.length > 0 ? (
            
            <div className={`${scopeId}-matrix-list`}>
              {allJobs.map((job) => (
                <div key={job.id} className={`${scopeId}-matrix-item`} onClick={() => view(job.uuid)}>
                  <div>
                    <h4 className={`${scopeId}-job-title`}>{job.title}</h4>
                  </div>
                  
                  <div className={`${scopeId}-item-meta-group`}>
                    <div className={`${scopeId}-cell-meta`}>
                      <FiClock size={14} /> <span>{job.status || "Full-Time"}</span>
                    </div>
                    <div className={`${scopeId}-action-trigger`}>
                      <FiChevronRight size={18} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

          ) : (
            
            /* High-End Minimalist Empty State */
            <div className={`${scopeId}-clean-empty`}>
              <div className={`${scopeId}-empty-right`}>
                <div style={{ marginBottom: '30px' }}>
                  <button onClick={openMyApplications} className={`${scopeId}-action-link`}>
                    View Application Log <FiArrowRight size={14} />
                  </button>
                </div>
                
                <div>
                  <h5 style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'rgba(255,255,255,0.4)', marginBottom: '12px' }}>
                    REACH OUT
                  </h5>
                  <a href="mailto:careers@werenium.com" className={`${scopeId}-email-block`}>
                    <span>careers@werenium.com</span>
                    <FiArrowRight size={14} />
                  </a>
                </div>
              </div>
            </div>

          )}
        </main>
      </div>
    </>
  );
}